import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AnimatePresence, MotionConfig } from 'framer-motion';
import ErrorBoundary from './components/ErrorBoundary';
import { AdminAuthProvider } from './contexts/AdminAuthContext';
import { UserAuthProvider, useUserAuth } from './contexts/UserAuthContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import './styles/mobile-fixes.css';

// Components that are needed on every page load (keep static)
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import { ToastProvider } from './components/Toast';

// Lazy-loaded global components (Fix #2 — defer heavy widgets)
const FloatingChildCard = lazy(() => import('./components/FloatingChildCard'));
const ChatWidget = lazy(() => import('./components/ChatWidget'));
const PaymentReminderBanner = lazy(() => import('./components/PaymentReminderBanner'));
const CookieConsent = lazy(() => import('./components/CookieConsent'));

// Lazy-loaded admin shell components (needed before admin routes render)
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const ProtectedRoute = lazy(() => import('./components/admin/ProtectedRoute'));
const ProtectedUserRoute = lazy(() => import('./components/ProtectedUserRoute'));

// Pages — lazy-loaded for route-based code splitting (Fix #1)
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const WhatWeDo = lazy(() => import('./pages/WhatWeDo'));
const Team = lazy(() => import('./pages/Team'));
const TeamMemberProfile = lazy(() => import('./pages/TeamMemberProfile'));
const Initiatives = lazy(() => import('./pages/Initiatives'));
const Events = lazy(() => import('./pages/Events'));
const Gallery = lazy(() => import('./pages/Gallery'));
const SponsorChild = lazy(() => import('./pages/SponsorChild'));
const ChildProfile = lazy(() => import('./pages/ChildProfile'));
const Contact = lazy(() => import('./pages/Contact'));
const Donate = lazy(() => import('./pages/Donate'));
const DonationSuccess = lazy(() => import('./pages/DonationSuccess'));
const Volunteer = lazy(() => import('./pages/Volunteer'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const AddChildForm = lazy(() => import('./pages/AddChildForm'));
const SponsorDashboard = lazy(() => import('./pages/SponsorDashboard'));
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const ResendVerification = lazy(() => import('./pages/ResendVerification'));
const UserDashboard = lazy(() => import('./pages/UserDashboard'));
const WidowApplication = lazy(() => import('./pages/WidowApplication'));
const VolunteerLogin = lazy(() => import('./pages/VolunteerLogin'));
const StaffClockIn = lazy(() => import('./pages/staff/StaffClockIn'));
const StaffDashboard = lazy(() => import('./pages/staff/StaffDashboard'));
const StaffSetPassword = lazy(() => import('./pages/staff/StaffSetPassword'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Admin Pages
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const Dashboard = lazy(() => import('./pages/admin/Dashboard'));
const ChildrenManagement = lazy(() => import('./pages/admin/ChildrenManagement'));
const AddChild = lazy(() => import('./pages/admin/AddChild'));
const EventsManagement = lazy(() => import('./pages/admin/EventsManagement'));
const AddEvent = lazy(() => import('./pages/admin/AddEvent'));
const EditEvent = lazy(() => import('./pages/admin/EditEvent'));
const InitiativesManagement = lazy(() => import('./pages/admin/InitiativesManagement'));
const AddInitiative = lazy(() => import('./pages/admin/AddInitiative'));
const EditInitiative = lazy(() => import('./pages/admin/EditInitiative'));
const PageManagement = lazy(() => import('./pages/admin/PageManagement'));
const ProgramsManagement = lazy(() => import('./pages/admin/ProgramsManagement'));
const CampaignManagement = lazy(() => import('./pages/admin/CampaignManagement'));
const VolunteerManagement = lazy(() => import('./pages/admin/VolunteerManagement'));
const UsersManagement = lazy(() => import('./pages/admin/UsersManagement'));
const PaymentManagement = lazy(() => import('./pages/admin/PaymentManagement'));
const FailedPayments = lazy(() => import('./pages/admin/FailedPayments'));
const DonationAllocations = lazy(() => import('./pages/admin/DonationAllocations'));
const SponsorshipManagement = lazy(() => import('./pages/admin/SponsorshipManagement'));
const ImageManagement = lazy(() => import('./pages/admin/ImageManagement'));
const Settings = lazy(() => import('./pages/admin/Settings'));
const Tasks = lazy(() => import('./pages/admin/Tasks'));
const Meetings = lazy(() => import('./pages/admin/Meetings'));
const ChildProgressManagement = lazy(() => import('./pages/admin/ChildProgressManagement'));
const VolunteerTaskManagement = lazy(() => import('./pages/admin/VolunteerTaskManagement'));
const NewsletterManagement = lazy(() => import('./pages/admin/NewsletterManagement'));
const ChatEscalations = lazy(() => import('./pages/admin/ChatEscalations'));
const ChildRequests = lazy(() => import('./pages/admin/ChildRequests'));
const WidowApplications = lazy(() => import('./pages/admin/WidowApplications'));
const WidowBeneficiaries = lazy(() => import('./pages/admin/WidowBeneficiaries'));
const Reports = lazy(() => import('./pages/admin/Reports'));
const OfficialLetterGenerator = lazy(() => import('./pages/admin/OfficialLetterGenerator'));
const PublicOfficialLetter = lazy(() => import('./pages/PublicOfficialLetter'));
const TeamManagement = lazy(() => import('./pages/admin/TeamManagement'));
const RecurringEventsManagement = lazy(() => import('./pages/admin/RecurringEventsManagement'));
const StudentEducatorAssignments = lazy(() => import('./pages/admin/StudentEducatorAssignments'));
const PastEventsManagement = lazy(() => import('./pages/admin/PastEventsManagement'));
const StaffAttendanceManagement = lazy(() => import('./pages/admin/StaffAttendanceManagement'));
const StaffManagement = lazy(() => import('./pages/admin/StaffManagement'));

// LMS Pages
const EducatorDashboard = lazy(() => import('./pages/lms/EducatorDashboard'));
const EducatorLogin = lazy(() => import('./pages/lms/EducatorLogin'));
const EducatorRegister = lazy(() => import('./pages/lms/EducatorRegister'));
const ScheduleClass = lazy(() => import('./pages/lms/ScheduleClass'));
const Classes = lazy(() => import('./pages/lms/Classes'));
const CreateQuiz = lazy(() => import('./pages/lms/CreateQuiz'));
const Assignments = lazy(() => import('./pages/lms/Assignments'));
const TimeTracking = lazy(() => import('./pages/lms/TimeTracking'));
const StudentPortal = lazy(() => import('./pages/lms/StudentPortal'));
const LMSAdmin = lazy(() => import('./pages/lms/LMSAdmin'));
const UploadResults = lazy(() => import('./pages/lms/UploadResults'));
const VolunteerDashboard = lazy(() => import('./pages/lms/VolunteerDashboard'));
const EducatorDashboardNew = lazy(() => import('./pages/educator/EducatorDashboard'));

// Lightweight loading fallback for Suspense boundaries
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-red-200 border-t-red-600 rounded-full animate-spin mx-auto mb-4" />
      <p className="text-gray-500 text-sm">Loading...</p>
    </div>
  </div>
);

const AppContent = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isLetterPrintRoute = location.pathname.startsWith('/letters/print/');
  const isStaffDashboard = location.pathname.startsWith('/staff/dashboard') || location.pathname.startsWith('/staff/set-password');
  const hideSiteChrome = isAdminRoute || isLetterPrintRoute || isStaffDashboard;
  const { user, isAuthenticated, authHeader } = useUserAuth();
  const userToken = localStorage.getItem('userToken');

  return (
    <div className="App min-h-screen bg-gray-50 w-full max-w-full overflow-x-hidden">
      {/* Skip-to-content link for keyboard navigation */}
      <a href="#main-content" className="skip-to-content">
        Skip to main content
      </a>
      {/* Payment Reminder Banner - shows for authenticated users */}
      {!hideSiteChrome && isAuthenticated() && user?.email && (
        <Suspense fallback={null}>
          <PaymentReminderBanner
            userEmail={user.email}
            authToken={userToken}
          />
        </Suspense>
      )}
      {!hideSiteChrome && <Navbar />}
      <ScrollToTop />
      <main id="main-content">
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <AnimatePresence mode="wait">
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/what-we-do" element={<WhatWeDo />} />
              <Route path="/what-we-do/team" element={<Team />} />
              <Route path="/what-we-do/team/:slug" element={<TeamMemberProfile />} />
              <Route path="/initiatives" element={<Initiatives />} />
              <Route path="/events" element={<Events />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/sponsor-child" element={<SponsorChild />} />
              <Route path="/child/:id" element={<ChildProfile />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/donate" element={<Donate />} />
              <Route path="/donation-success" element={<DonationSuccess />} />
              <Route path="/volunteer" element={<Volunteer />} />
              <Route path="/widow-application" element={<WidowApplication />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/add-child" element={<AddChildForm />} />
              <Route path="/sponsor-dashboard" element={<SponsorDashboard />} />
              <Route path="/clock-in" element={<StaffClockIn />} />
              <Route path="/staff/clock-in" element={<StaffClockIn />} />
              <Route path="/staff" element={<Navigate to="/staff/dashboard" replace />} />
              <Route path="/staff/dashboard" element={
                <ProtectedUserRoute allowedRoles={["STAFF", "ADMIN", "SUPER_ADMIN"]} loginPath="/login">
                  <StaffDashboard />
                </ProtectedUserRoute>
              } />
              <Route path="/staff/set-password" element={<StaffSetPassword />} />

              {/* User Authentication Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/resend-verification" element={<ResendVerification />} />
              <Route path="/dashboard" element={<UserDashboard />} />
              <Route path="/educator/dashboard" element={
                <ProtectedUserRoute allowedRoles={["EDUCATOR"]} loginPath="/educator/login">
                  <EducatorDashboardNew />
                </ProtectedUserRoute>
              } />

              {/* Admin Routes */}
              <Route path="/aofadmin" element={<AdminLogin />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={
                <ProtectedRoute>
                  <AdminLayout><Dashboard /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/children" element={
                <ProtectedRoute>
                  <AdminLayout><ChildrenManagement /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/children/add" element={
                <ProtectedRoute>
                  <AdminLayout><AddChild /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/events" element={
                <ProtectedRoute>
                  <AdminLayout><EventsManagement /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/events/add" element={
                <ProtectedRoute>
                  <AdminLayout><AddEvent /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/events/edit/:id" element={
                <ProtectedRoute>
                  <AdminLayout><EditEvent /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/initiatives" element={
                <ProtectedRoute>
                  <AdminLayout><InitiativesManagement /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/initiatives/add" element={
                <ProtectedRoute>
                  <AdminLayout><AddInitiative /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/initiatives/edit/:id" element={
                <ProtectedRoute>
                  <AdminLayout><EditInitiative /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/programs" element={
                <ProtectedRoute>
                  <AdminLayout><ProgramsManagement /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/campaigns" element={
                <ProtectedRoute>
                  <AdminLayout><CampaignManagement /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/child-requests" element={
                <ProtectedRoute>
                  <AdminLayout><ChildRequests /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/widow-applications" element={
                <ProtectedRoute>
                  <AdminLayout><WidowApplications /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/widow-beneficiaries" element={
                <ProtectedRoute>
                  <AdminLayout><WidowBeneficiaries /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/volunteers" element={
                <ProtectedRoute>
                  <AdminLayout><VolunteerManagement /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/users" element={
                <ProtectedRoute>
                  <AdminLayout><UsersManagement /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/donations" element={
                <ProtectedRoute>
                  <AdminLayout><PaymentManagement /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/donation-allocations" element={
                <ProtectedRoute>
                  <AdminLayout><DonationAllocations /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/failed-payments" element={
                <ProtectedRoute>
                  <AdminLayout><FailedPayments /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/sponsorships" element={
                <ProtectedRoute>
                  <AdminLayout><SponsorshipManagement /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/images" element={
                <ProtectedRoute>
                  <AdminLayout><ImageManagement /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/reports" element={
                <ProtectedRoute>
                  <AdminLayout><Reports /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/settings" element={
                <ProtectedRoute>
                  <AdminLayout><Settings /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/pages" element={
                <ProtectedRoute>
                  <AdminLayout><PageManagement /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/tasks" element={
                <ProtectedRoute>
                  <AdminLayout><Tasks /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/meetings" element={
                <ProtectedRoute>
                  <AdminLayout><Meetings /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/child-progress" element={
                <ProtectedRoute>
                  <AdminLayout><ChildProgressManagement /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/volunteer-tasks" element={
                <ProtectedRoute>
                  <AdminLayout><VolunteerTaskManagement /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/newsletter" element={
                <ProtectedRoute>
                  <AdminLayout><NewsletterManagement /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/letters" element={
                <ProtectedRoute>
                  <AdminLayout><OfficialLetterGenerator /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/letters/print/:reference" element={<PublicOfficialLetter />} />
              <Route path="/admin/support/escalations" element={
                <ProtectedRoute>
                  <AdminLayout><ChatEscalations /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/team" element={
                <ProtectedRoute>
                  <AdminLayout><TeamManagement /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/recurring-events" element={
                <ProtectedRoute>
                  <AdminLayout><RecurringEventsManagement /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/assignments" element={
                <ProtectedRoute>
                  <AdminLayout><StudentEducatorAssignments /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/past-events" element={
                <ProtectedRoute>
                  <AdminLayout><PastEventsManagement /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/attendance" element={
                <ProtectedRoute>
                  <AdminLayout><StaffAttendanceManagement /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/staff" element={
                <ProtectedRoute>
                  <AdminLayout><StaffManagement /></AdminLayout>
                </ProtectedRoute>
              } />

              {/* User Login Routes */}
              <Route path="/educator/login" element={<EducatorLogin />} />
              <Route path="/educator/register" element={<EducatorRegister />} />
              <Route path="/volunteer/login" element={<VolunteerLogin />} />

              {/* LMS Routes (educator protected) */}
              <Route path="/lms" element={
                <ProtectedUserRoute allowedRoles={["EDUCATOR"]} loginPath="/educator/login">
                  <EducatorDashboard />
                </ProtectedUserRoute>
              } />
              <Route path="/lms/schedule-class" element={
                <ProtectedUserRoute allowedRoles={["EDUCATOR"]} loginPath="/educator/login">
                  <ScheduleClass />
                </ProtectedUserRoute>
              } />
              <Route path="/lms/classes" element={
                <ProtectedUserRoute allowedRoles={["EDUCATOR"]} loginPath="/educator/login">
                  <Classes />
                </ProtectedUserRoute>
              } />
              <Route path="/lms/create-quiz" element={
                <ProtectedUserRoute allowedRoles={["EDUCATOR"]} loginPath="/educator/login">
                  <CreateQuiz />
                </ProtectedUserRoute>
              } />
              <Route path="/lms/assignments" element={
                <ProtectedUserRoute allowedRoles={["EDUCATOR"]} loginPath="/educator/login">
                  <Assignments />
                </ProtectedUserRoute>
              } />
              <Route path="/lms/time-tracking" element={
                <ProtectedUserRoute allowedRoles={["EDUCATOR"]} loginPath="/educator/login">
                  <TimeTracking />
                </ProtectedUserRoute>
              } />
              <Route path="/lms/student" element={
                <ProtectedUserRoute allowedRoles={["STUDENT"]} loginPath="/login">
                  <StudentPortal />
                </ProtectedUserRoute>
              } />
              <Route path="/volunteer-dashboard" element={
                <ProtectedUserRoute allowedRoles={["VOLUNTEER"]} loginPath="/volunteer/login">
                  <VolunteerDashboard />
                </ProtectedUserRoute>
              } />
              {/* LMS Routes (admin) */}
              <Route path="/admin/lms" element={
                <ProtectedRoute>
                  <AdminLayout><LMSAdmin /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/lms/students" element={
                <ProtectedRoute>
                  <AdminLayout><LMSAdmin /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/lms/educators" element={
                <ProtectedRoute>
                  <AdminLayout><LMSAdmin /></AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/lms/upload-results" element={
                <ProtectedRoute>
                  <AdminLayout><UploadResults /></AdminLayout>
                </ProtectedRoute>
              } />

              {/* 404 Catch-all */}
              <Route path="*" element={<NotFound />} />
          </Routes>
        </AnimatePresence>
        </Suspense>
      </ErrorBoundary>
      </main>
      {!hideSiteChrome && (
        <Suspense fallback={null}>
          <FloatingChildCard />
        </Suspense>
      )}
      {!hideSiteChrome && (
        <Suspense fallback={null}>
          <ChatWidget />
        </Suspense>
      )}
      {!hideSiteChrome && <Footer />}

      {/* Cookie Consent Banner */}
      {!hideSiteChrome && (
        <Suspense fallback={null}>
          <CookieConsent />
        </Suspense>
      )}
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <ToastProvider position="top-right">
        <div className="w-full max-w-full overflow-x-hidden">
          <MotionConfig reducedMotion="user">
            <CurrencyProvider>
              <AdminAuthProvider>
                <UserAuthProvider>
                  <Router>
                    <AppContent />
                  </Router>
                </UserAuthProvider>
              </AdminAuthProvider>
            </CurrencyProvider>
          </MotionConfig>
        </div>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
